import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@cmsgov/ds-medicare-gov'

import openLiveChat, { openInterimLiveChat } from '../../utils/openLiveChat'
import events, { Event } from 'pubsub/events'
import { useLegacyChatEvent } from 'pubsub/hooks'
import { Categories } from 'constants/analytics'
import { useSelector } from 'react-redux'
import { getUserLoggedInState } from '../../redux/reducers/users'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { createAccountUrl, UnauthChatMenu } from './UnauthChatMenu'
import { ChatBlockedAlert } from './ChatBlockedAlert'
import { makeAuthChatEventCfg, makeUnauthGuestChatEventCfg, makeUnauthLoginEventCfg, makeUnauthRegisterEventCfg, popupBlockedEventCfg } from './eventConfig'
import eventsV2 from 'pubsub/eventsV2'

export interface LiveChatMenuProps {
  openChatAsPopup: boolean
  viewpage?: string
  loginUrl: string
}

export const LiveChatMenu: React.FC<LiveChatMenuProps> = ({
  openChatAsPopup,
  viewpage,
  loginUrl
}) => {
  const { t, i18n } = useTranslation('liveChat')
  const isAuthenticated = useSelector(getUserLoggedInState)
  const [chatBlocked, setChatBlocked] = useState(false)

  const lang: 'en' | 'es' = i18n.language as unknown as 'en' | 'es'
  const flags = useFlags()
  const buttonTextTranslationKey = flags.newLiveChatRollout ? 'auth.chatNow' : 'buttonText'

  // Event factories
  const makeUnauthLoginEvent = useLegacyChatEvent(makeUnauthLoginEventCfg(loginUrl))
  const makeUnauthRegisterEvent = useLegacyChatEvent(makeUnauthRegisterEventCfg(createAccountUrl))
  const makeUnauthGuestChatEvent = useLegacyChatEvent(makeUnauthGuestChatEventCfg())
  const makeAuthChatEvent = useLegacyChatEvent(makeAuthChatEventCfg())
  const makePopupBlockedEvent = useLegacyChatEvent(popupBlockedEventCfg)

  // Only fires for legacy chat; replaced by v2 events if feature flag set
  const trackClickEvent = () => {
    const options: Event = {
      authenticated: isAuthenticated,
      text: t(buttonTextTranslationKey),
      targetLocation: 'Open live chat',
      heading: 'Chat',
      category: Categories.PersonalNav,
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      // Since we are sending Core event via new event system, don't do it here
      sendToCoreAPI: false,
      isMessageCenter: false
    }

    events.publish(options)
  }

  const handleLiveChatClick = async () => {
    setChatBlocked(false)
    try {
      if (flags.liveChatUseDirectChatLink) {
        await openLiveChat({
          isAuthenticated,
          asPopup: openChatAsPopup,
          viewpage,
          lang
        })
      } else {
        await openInterimLiveChat({
          isAuthenticated,
          asPopup: openChatAsPopup,
          viewpage,
          lang
        })
      }
    } catch (error) {
      setChatBlocked(true)

      eventsV2.publish(makePopupBlockedEvent())
    }
  }

  const handleLegacyChatClick = () => {
    trackClickEvent()
    handleLiveChatClick()
  }

  const handleAuthChatClick = () => {
    eventsV2.publish(makeAuthChatEvent())
    handleLiveChatClick()
  }

  const handleGuestChatClick = () => {
    eventsV2.publish(makeUnauthGuestChatEvent())
    handleLiveChatClick()
  }

  // This structure allows easier removal of legacy version when time comes
  if (!flags.newLiveChatRollout) {
    return (
      <div className="ch-c-livechat-menu">
        <p className="ds-text-body--md ds-u-margin-bottom--3">{t('message')}</p>
        <Button
          title="Live Chat - Opens in a new window"
          className="ds-c-button--solid"
          data-testid="live-chat-btn"
          onClick={handleLegacyChatClick}
          type="button"
        >
          {t('buttonText')}
        </Button>
      </div>
    )
  }

  if (isAuthenticated) {
    return (
      <div className="ch-c-livechat-menu">
        <p className="ds-text-body--md ds-u-margin-bottom--3">{t('common.description')}</p>
        <Button
          title="Live Chat - Opens in a new window"
          className="ds-c-button--solid"
          data-testid="live-chat-btn"
          onClick={handleAuthChatClick}
          type="button"
        >
          {t('auth.chatNow')}
        </Button>
        {chatBlocked && <ChatBlockedAlert />}
      </div>
    )
  }

  return (
  <UnauthChatMenu
    loginUrl={loginUrl}
    onOpenGuestChat={handleGuestChatClick}
    onClickLogin={() => eventsV2.publish(makeUnauthLoginEvent())}
    onClickRegister={() => eventsV2.publish(makeUnauthRegisterEvent())}
    chatBlocked={chatBlocked}
  />
  )
}
